import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';

import { TwcSwitchButtonComponent, TwcSwitchComponent } from '@topcon/twc-angular';

export interface IconSwitchOption {
    label: string;
    value: any;
    icon: string;
}

export type IconSwitchOptions = [IconSwitchOption, IconSwitchOption];

/**
 * Two options switch component with icons over option buttons
 */
@Component({
    selector: 'cw-icon-switch',
    templateUrl: './icon-switch.component.html',
    styleUrls: ['./icon-switch.component.scss'],
    standalone: false,
})
export class IconSwitchComponent implements AfterViewInit, OnDestroy {
    /**
     * Pair of options to be shown in the component
     */
    @Input() switchOptions!: IconSwitchOptions;
    /**
     * Initial default selected option
     */
    @Input() defaultOptionIndex: number | null = null;
    /**
     * Disable or enable the component
     */
    @Input() disabled = false;
    /**
     * Blocks the interaction with the component
     */
    @Input() readonly = false;

    /**
     * Emits when the selection changes
     */
    @Output() selectionChanged = new EventEmitter<any>();

    @ViewChild(TwcSwitchComponent, { static: false }) switchComponent!: TwcSwitchComponent;
    @ViewChildren(TwcSwitchButtonComponent, { read: ElementRef }) switchButtons!: QueryList<ElementRef<HTMLElement>>;

    slidingLayerStyles: Record<string, string> = {};

    private resizeObservers: ResizeObserver[] = [];

    readonly NOT_SELECTED = -1;

    constructor(private cdr: ChangeDetectorRef) {}

    ngAfterViewInit(): void {
        this.selectOption(this.defaultOptionIndex, false);
    }

    ngOnDestroy(): void {
        this.resizeObservers.forEach((observer) => observer.disconnect());
    }

    /**
     * Method called from the view when the component changes
     * @param twcSwitch The switch component
     */
    onChange(twcSwitch: TwcSwitchComponent = this.switchComponent): void {
        if (this.disabled || this.readonly) {
            return;
        }

        const index = twcSwitch.selectedItem?.data;
        const value = this.switchOptions?.[index]?.value;

        this.selectionChanged.emit(value);
    }

    /**
     * Select option by index
     * @param index Index to select
     * @param shouldEmit Emits the selected value, default true
     */
    selectOption(index?: number | null, shouldEmit = true): void {
        if (this.disabled || this.readonly) {
            return;
        }

        this.switchComponent.selectedIndex = this.isIndexInRange(index) ? index : this.NOT_SELECTED;

        if (shouldEmit) {
            this.onChange();
        }
    }

    /**
     * Clears current selection
     */
    clearSelection(): void {
        if (this.disabled) {
            return;
        }

        this.switchComponent.selectedIndex = this.NOT_SELECTED;
        this.onChange();
    }

    /**
     * If some element has been selected
     */
    isAnySelected(): boolean {
        return this.isIndexInRange(this.switchComponent?.selectedIndex);
    }

    /**
     * Checks if the option has icons
     */
    hasIcons(): boolean {
        const icons = this.switchOptions.map((option) => option.icon).filter(Boolean);
        return icons.length === 2;
    }

    /**
     * Checks if the given index is active
     * @param index Index to check
     */
    isActive(index: number): boolean {
        return this.switchComponent?.selectedIndex === index;
    }

    /**
     * Checks if the given index is valid
     * @param index Index to check
     */
    isIndexInRange(index: any): index is number {
        const isNumeric = typeof index === 'number' && index >= 0;
        const maxIndex = this.switchOptions.length - 1;
        if (isNumeric && index > maxIndex) {
            throw new Error('Index out of range');
        }

        return isNumeric;
    }

    /**
     * Get the labels of the options
     */
    get labels(): [string, string] {
        return this.switchOptions.map((option) => option.label) as [string, string];
    }

    /**
     * Get the icons of the options
     */
    get icons(): [string, string] {
        const icons = this.switchOptions.map((option) => option.icon).filter(Boolean);

        if (icons.length !== 2) {
            throw new Error('Both icons must be set');
        }

        return icons as [string, string];
    }
}
